import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search } from 'lucide-react';
import { Container } from '../components/layout/container';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

export default function NotFoundPage() {
  const { t } = useLanguage();
  const notFoundData = t.notFound;

  return (
    <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center">
      <SEO 
        title={notFoundData?.seo?.title || "404 - Page Not Found | Nandan Priyadarshi"}
        description={notFoundData?.seo?.description || "The page you're looking for doesn't exist. Return to Nandan Priyadarshi's portfolio homepage."}
        noindex
      />
      
      <Container>
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 404 Number */}
          <motion.div
            className="mb-8"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="text-8xl sm:text-9xl font-bold bg-gradient-to-r from-white via-slate-300 to-slate-500 bg-clip-text text-transparent">
              404
            </h1>
          </motion.div>

          {/* Icon */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <Search className="h-10 w-10 text-white/30" />
            </div>
          </motion.div>

          {/* Message */}
          <motion.div
            className="space-y-4 mb-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl sm:text-3xl font-semibold text-white">
              {notFoundData?.title || 'Page Not Found'}
            </h2>
            <p className="text-white/50 max-w-md mx-auto">
              {notFoundData?.description || "The page you're looking for doesn't exist or has been moved. Let's get you back on track."}
            </p>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button asChild size="lg">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                {notFoundData?.backHome || 'Back to Home'}
              </Link>
            </Button>
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact-me">
                {notFoundData?.contactMe || 'Contact Me'}
              </Link>
            </Button>
          </motion.div>

          {/* Helpful Links */}
          <motion.div
            className="mt-12 pt-8 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-sm text-white/40 mb-4">{notFoundData?.quickLinks || 'Quick Links:'}</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/" className="text-white/60 hover:text-white transition-colors">
                {notFoundData?.home || 'Home'}
              </Link>
              <Link to="/experience" className="text-white/60 hover:text-white transition-colors">
                {notFoundData?.experience || 'Experience'}
              </Link>
              <Link to="/contact-me" className="text-white/60 hover:text-white transition-colors">
                {notFoundData?.contact || 'Contact'}
              </Link>
              <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors">
                {notFoundData?.privacy || 'Privacy'}
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  );
}
